
import React from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { CheckCircle, Gem, Coins, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuth } from "@/context/AuthContext";

const Cmime = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleBuyCoins = () => {
    if (isAuthenticated) {
      navigate("/coins");
    } else {
      navigate("/auth");
    }
  };

  // JSON-LD structured data for Product
  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Kredite për Shpallje në Tregu.al",
    "description": "Sistemin tonë të krediteve për të publikuar shpallje me çmime të përballueshme.",
    "offers": [
      {
        "@type": "Offer",
        "name": "Paketë Fillestare",
        "price": "4.90",
        "priceCurrency": "EUR",
        "description": "10 kredite për shpallje"
      },
      {
        "@type": "Offer",
        "name": "Paketë Standarde",
        "price": "9.90",
        "priceCurrency": "EUR",
        "description": "20 kredite për shpallje"
      },
      {
        "@type": "Offer",
        "name": "Paketë Pro",
        "price": "19.90",
        "priceCurrency": "EUR",
        "description": "50 kredite për shpallje"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Çmimet dhe Paketat | Tregu.al - Platforma Online e Shqipërisë</title>
        <meta
          name="description"
          content="Zgjidh planin e duhur për nevojat tuaja. Shikoni çmimet dhe paketat e krediteve të Tregu.al për publikimin e shpalljeve dhe promovimin e produkteve."
        />
        <meta name="keywords" content="çmimet tregu.al, paketa, kredite shpallje, publikim shpallje, tarifa tregu.al" />
        <meta property="og:title" content="Çmimet dhe Paketat | Tregu.al" />
        <meta property="og:description" content="Zgjidh planin e duhur për nevojat tuaja. Shikoni çmimet dhe paketat e krediteve të Tregu.al." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tregu.al/cmime" />
        <meta property="og:image" content="https://tregu.al/logo512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Çmimet dhe Paketat | Tregu.al" />
        <meta name="twitter:description" content="Zgjidh planin e duhur për nevojat tuaja. Shikoni çmimet dhe paketat e krediteve të Tregu.al." />
        <link rel="canonical" href="https://tregu.al/cmime" />
        <script type="application/ld+json">
          {JSON.stringify(pricingSchema)}
        </script>
      </Helmet>

      <Navbar />

      <main className="py-16 bg-gray-50">
        <Container>
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Çmimet tona</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Përdorni sistemin tonë të krediteve për të publikuar shpallje me
              çmime të përballueshme.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
            <div className="p-6 border-b">
              <div className="flex items-center justify-center mb-4">
                <Coins className="h-8 w-8 text-amber-500 mr-2" />
                <h2 className="text-2xl font-bold">Sistemi i Krediteve</h2>
              </div>
              <p className="text-center text-gray-600 mb-6">
                Çdo kredit ju lejon të publikoni një shpallje të re
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Plus className="h-5 w-5 text-green-500 mr-2" />
                    Kredite Falas
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>5 kredite ne muaj falas pas regjistrimit</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Kredite falas nuk skadojnë</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Plus className="h-5 w-5 text-green-500 mr-2" />
                    Kredite Shtesë
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>
                        Në dispozicion pas përdorimit të krediteve falas
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Vetëm 49 cent për çdo kredit</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Paketa me zbritje në dispozicion</span>
                    </li>
                  </ul>
                </div>
              </div>

              <Button
                className="w-full bg-green-500 hover:bg-green-600"
                onClick={handleBuyCoins}
              >
                Blej Kredite Tani
              </Button>
            </div>

            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">
                Çfarë përfitoni me sistemin e krediteve:
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Paguani vetëm për aq shpallje sa ju nevojiten</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Pa abonim mujor ose angazhim afatgjatë</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Kredite që nuk skadojnë kurrë</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Paketa ekonomike për blerjet në sasi</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 max-w-3xl mx-auto bg-amber-50 p-6 rounded-lg border border-amber-200">
            <h2 className="text-xl font-bold text-amber-800 mb-4 flex items-center">
              <Gem className="h-5 w-5 text-amber-500 mr-2" />
              Çfarë janë shpalljet Premium?
            </h2>
            <p className="text-amber-700 mb-4">
              Shpalljet Premium marrin më shumë vëmendje dhe shfaqen në vende të
              veçanta në platformë:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start text-amber-700">
                <CheckCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>Shfaqen më lart në rezultatet e kërkimit</span>
              </li>
              <li className="flex items-start text-amber-700">
                <CheckCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  Shënohen me një shenjë të veçantë që tërheq vëmendjen e
                  blerësve
                </span>
              </li>
              <li className="flex items-start text-amber-700">
                <CheckCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  I disponueshëm për blerje veçmas nga sistemit i krediteve
                </span>
              </li>
            </ul>
          </div>

          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold mb-4">Pyetje të Shpeshta</h2>
            <div className="max-w-3xl mx-auto text-left space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  A skadojnë kreditë?
                </h3>
                <p className="text-gray-600">
                  Jo, kreditet që blini nuk skadojnë kurrë dhe mund t'i përdorni
                  kur të dëshironi.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  A mund të transferoj kredite?
                </h3>
                <p className="text-gray-600">
                  Aktualisht, kreditet nuk mund të transferohen midis llogarive
                  të ndryshme.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  Si paguaj për kredite?
                </h3>
                <p className="text-gray-600">
                  Pranojmë pagesa me karta krediti dhe PayPal.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  A mund të marr rimbursim për kreditet e papërdorura?
                </h3>
                <p className="text-gray-600">
                  Kreditet janë të pakthyeshme, por nuk skadojnë kurrë, kështu
                  që mund t'i përdorni kurdo që të dëshironi.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </main>

      <Footer />
    </>
  );
};

export default Cmime;
